import React, { useEffect, useMemo, useState, Children } from 'react';
import { dashboardSeed } from '../lib/data';
import { ActivityIcon, TrendingUpIcon, PercentIcon, DollarSignIcon, ClockIcon, BarChart3Icon, CheckCircleIcon, ZapIcon, FileTextIcon } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
export function Dashboard() {
  const [data, setData] = useState(dashboardSeed);
  const [now, setNow] = useState(new Date());
  // Set page title
  useEffect(() => {
    document.title = 'Mint | Dashboard';
  }, []);
  // clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  // gentle live ticks (no blinking)
  useEffect(() => {
    const t = setInterval(() => {
      setData(prev => ({
        ...prev,
        agents: prev.agents.map((a, i) => {
          // small bounded jitter per agent
          const jitter = i === 0 ? 2 : 1;
          const ips = Math.max(1, a.ips + Math.round((Math.random() - 0.5) * 2 * jitter));
          const processed = a.processed + Math.max(1, Math.round(ips * 0.6));
          return {
            ...a,
            ips,
            processed
          };
        })
      }));
    }, 1500);
    return () => clearInterval(t);
  }, []);
  const hhmmss = useMemo(() => {
    // Add 3 hours to get East Coast time
    const estTime = new Date(now.getTime() + 3 * 60 * 60 * 1000);
    return estTime.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }, [now]);
  // helpers
  const fmtInt = (n: number) => n.toLocaleString();
  const fmtUSD = (n: number) => `$${n.toLocaleString(undefined, {
    maximumFractionDigits: 1
  })}`;
  // Generate sparkline data
  const generateSparklineData = (points: number = 20) => {
    const data = [];
    for (let i = 0; i < points; i++) {
      data.push(20 + Math.random() * 10 - i * 0.2);
    }
    return data;
  };
  const sparklineData = useMemo(() => generateSparklineData(), []);
  const Sparkline = ({
    data
  }: {
    data: number[];
  }) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min;
    const points = data.map((value, index) => {
      const x = index / (data.length - 1) * 100;
      const y = 30 - (value - min) / range * 28;
      return `${x},${y}`;
    }).join(' ');
    return <svg className="w-full h-8 mt-2" viewBox="0 0 100 30" data-id="element-1384">
        <polyline points={points} fill="none" stroke="#6b7280" strokeWidth="1.5" data-id="element-1385" />
      </svg>;
  };
  return <div className="min-h-screen bg-gray-50 px-6 py-6" data-id="element-1386">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-1387">
        <h1 className="text-3xl font-semibold text-gray-900" data-id="element-1388">Dashboard</h1>
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm" data-id="element-1389">
          <div className="relative" data-id="element-1390">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" data-id="element-1391" />
            <span className="absolute inset-0 h-2 w-2 rounded-full bg-emerald-500 animate-ping" data-id="element-1392" />
          </div>
          <span className="text-sm text-gray-700" data-id="element-1393">Live, {hhmmss} EST</span>
        </div>
      </div>
      {/* Validation Funnel */}
      <div className="mb-6" data-id="element-1394">
        <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-1395">
          Validation Funnel
        </h2>
        <div className="grid grid-cols-4 gap-4" data-id="element-1396">
          <Card data-id="element-1397">
            <div className="pb-2 pt-4 px-4" data-id="element-1398">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1399">
                Invoices Processed
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1400">
              <div className="flex items-baseline gap-2" data-id="element-1401">
                <div className="text-3xl font-bold" data-id="element-1402">498</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1403">
                  ↑ 2.3%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1404">Past 30 Days</div>
            </CardContent>
          </Card>
          <Card data-id="element-1405">
            <div className="pb-2 pt-4 px-4" data-id="element-1406">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1407">
                Pipeline (Active)
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1408">
              <div className="flex items-baseline gap-2" data-id="element-1409">
                <div className="text-3xl font-bold" data-id="element-1410">847,293</div>
                <span className="text-xs text-gray-600 font-medium" data-id="element-1411">
                  → 0.1%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1412">Receivables to Verify</div>
            </CardContent>
          </Card>
          <Card data-id="element-1413">
            <div className="pb-2 pt-4 px-4" data-id="element-1414">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1415">
                Active Queue
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1416">
              <div className="flex items-baseline gap-2" data-id="element-1417">
                <div className="text-3xl font-bold" data-id="element-1418">8,473</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1419">
                  ↑ 5.2%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1420">In Processing</div>
            </CardContent>
          </Card>
          <Card data-id="element-1421">
            <div className="pb-2 pt-4 px-4" data-id="element-1422">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1423">Accuracy</div>
            </div>
            <CardContent className="pt-0" data-id="element-1424">
              <div className="flex items-baseline gap-2" data-id="element-1425">
                <div className="text-3xl font-bold" data-id="element-1426">99.7%</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1427">
                  ↑ 0.2%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1428">≥ 0.85 Confidence</div>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Engine Performance */}
      <div className="mb-6" data-id="element-1429">
        <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-1430">
          Engine Performance
        </h2>
        <div className="grid grid-cols-4 gap-4" data-id="element-1431">
          <Card data-id="element-1432">
            <div className="pb-2 pt-4 px-4" data-id="element-1433">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1434">
                Processing Volume
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1435">
              <div className="text-3xl font-bold" data-id="element-1436">$63.4M</div>
              <Sparkline data={sparklineData} data-id="element-1437" />
            </CardContent>
          </Card>
          <Card data-id="element-1438">
            <div className="pb-2 pt-4 px-4" data-id="element-1439">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1440">Throughput</div>
            </div>
            <CardContent className="pt-0" data-id="element-1441">
              <div className="flex items-baseline gap-2" data-id="element-1442">
                <div className="text-3xl font-bold" data-id="element-1443">855 TPS</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1444">
                  ↑ 1.8%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1445">Transactions/Second</div>
            </CardContent>
          </Card>
          <Card data-id="element-1446">
            <div className="pb-2 pt-4 px-4" data-id="element-1447">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1448">
                Processing Time
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1449">
              <div className="flex items-baseline gap-2" data-id="element-1450">
                <div className="text-3xl font-bold" data-id="element-1451">36s</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1452">
                  ↓ 3.1%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1453">Average Latency</div>
            </CardContent>
          </Card>
          <Card data-id="element-1454">
            <div className="pb-2 pt-4 px-4" data-id="element-1455">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1456">
                Success Rate
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1457">
              <div className="flex items-baseline gap-2" data-id="element-1458">
                <div className="text-3xl font-bold" data-id="element-1459">99.7%</div>
                <span className="text-xs text-gray-600 font-medium" data-id="element-1460">
                  → 0.0%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1461">≥ 0.85 Confidence</div>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Agentic Funnel */}
      <div data-id="element-1462">
        <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-1463">
          Agentic Funnel
        </h2>
        <div className="grid grid-cols-5 gap-4" data-id="element-1464">
          <Card data-id="element-1465">
            <div className="pb-2 pt-4 px-4" data-id="element-1466">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1467">
                Discovery Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1468">
              <div className="flex items-baseline gap-2" data-id="element-1469">
                <div className="text-2xl font-bold" data-id="element-1470">
                  {(data.agents[0].processed / 1000000).toFixed(2)}M
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1471">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1472">Invoices Indexed</div>
            </CardContent>
          </Card>
          <Card data-id="element-1473">
            <div className="pb-2 pt-4 px-4" data-id="element-1474">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1475">
                Extraction Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1476">
              <div className="flex items-baseline gap-2" data-id="element-1477">
                <div className="text-2xl font-bold" data-id="element-1478">
                  {Math.round(data.agents[1].processed / 1000)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1479">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1480">Fields Extracted</div>
            </CardContent>
          </Card>
          <Card data-id="element-1481">
            <div className="pb-2 pt-4 px-4" data-id="element-1482">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1483">
                Validation Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1484">
              <div className="flex items-baseline gap-2" data-id="element-1485">
                <div className="text-2xl font-bold" data-id="element-1486">
                  {Math.round(data.agents[2].processed / 1000)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1487">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1488">Links Verified</div>
            </CardContent>
          </Card>
          <Card data-id="element-1489">
            <div className="pb-2 pt-4 px-4" data-id="element-1490">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1491">
                Underwriting Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1492">
              <div className="flex items-baseline gap-2" data-id="element-1493">
                <div className="text-2xl font-bold" data-id="element-1494">
                  {Math.round(data.agents[3].processed / 1000)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1495">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1496">Receivables Scored</div>
            </CardContent>
          </Card>
          <Card data-id="element-1497">
            <div className="pb-2 pt-4 px-4" data-id="element-1498">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1499">
                On-Chain Audit
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1500">
              <div className="flex items-baseline gap-2" data-id="element-1501">
                <div className="text-2xl font-bold" data-id="element-1502">
                  {(data.agents[4].processed / 1000).toFixed(1)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1503">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1504">Blocks Verified</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}